import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import AdminSidebar from '@/components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Mail, Send, CalendarIcon, Bell, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReminders = () => {
  const { getAuthHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [amount, setAmount] = useState('275');
  const [dueDate, setDueDate] = useState(null);
  const [vehicleName, setVehicleName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, bookingsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/customers`, getAuthHeader()),
        axios.get(`${API_URL}/bookings`, getAuthHeader()),
        axios.get(`${API_URL}/reminder-logs`, getAuthHeader())
      ]);
      setCustomers(customersRes.data);
      setBookings(bookingsRes.data);
      setReminderLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!selectedCustomer || !amount || !dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSendingReminder(true);
    try {
      await axios.post(`${API_URL}/send-payment-reminder`, {
        customer_id: selectedCustomer,
        amount: parseFloat(amount),
        due_date: format(dueDate, 'MMMM d, yyyy'),
        vehicle_name: vehicleName || null
      }, getAuthHeader());

      toast.success('Payment reminder sent successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const resetForm = () => {
    setSelectedCustomer('');
    setAmount('275');
    setDueDate(null);
    setVehicleName('');
  };

  const handleQuickReminder = async (booking) => {
    setSendingReminder(true);
    try {
      // Calculate next payment date (7 days from start or from today)
      const startDate = new Date(booking.start_date);
      const today = new Date();
      let nextPaymentDate = new Date(startDate);
      while (nextPaymentDate <= today) {
        nextPaymentDate.setDate(nextPaymentDate.getDate() + 7);
      }

      await axios.post(`${API_URL}/send-payment-reminder`, {
        customer_id: booking.customer_id,
        amount: 275,
        due_date: format(nextPaymentDate, 'MMMM d, yyyy'),
        vehicle_name: booking.vehicle_name
      }, getAuthHeader());

      toast.success(`Reminder sent to ${booking.customer_name}!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const activeBookings = bookings.filter(b => ['confirmed', 'active'].includes(b.status));

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-reminders">
      <AdminSidebar />
      
      <main className="flex-1 p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Payment Reminders</h1>
            <p className="text-slate-500">Send payment reminders to customers via email</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="send-reminder-btn">
                <Mail className="w-4 h-4 mr-2" />
                Send Reminder
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="reminder-dialog">
              <DialogHeader>
                <DialogTitle>Send Payment Reminder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Select Customer *</Label>
                  <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                    <SelectTrigger data-testid="reminder-customer-select">
                      <SelectValue placeholder="Choose a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name} ({customer.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Amount Due ($) *</Label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="275"
                    data-testid="reminder-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="reminder-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dueDate ? format(dueDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dueDate}
                        onSelect={setDueDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Vehicle (Optional)</Label>
                  <Input
                    value={vehicleName}
                    onChange={(e) => setVehicleName(e.target.value)}
                    placeholder="e.g., 2015 Ford Fusion"
                    data-testid="reminder-vehicle-input"
                  />
                </div>

                <Button
                  onClick={handleSendReminder}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={sendingReminder}
                  data-testid="send-reminder-submit"
                >
                  {sendingReminder ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reminder Email
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Quick Reminders for Active Bookings */}
        <Card className="bg-white mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Active Rentals - Quick Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeBookings.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No active rentals</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Weekly Rate</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.customer_name}</p>
                          <p className="text-xs text-slate-500">{booking.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{booking.vehicle_name}</TableCell>
                      <TableCell>{format(new Date(booking.start_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell>$275</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => handleQuickReminder(booking)}
                          disabled={sendingReminder}
                          className="bg-amber-500 hover:bg-amber-600"
                          data-testid={`quick-reminder-${booking.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Send Reminder
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Reminder History */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Reminder History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reminderLogs.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No reminders sent yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reminderLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">{log.customer_name}</TableCell>
                      <TableCell>{log.customer_email}</TableCell>
                      <TableCell>${log.amount}</TableCell>
                      <TableCell>{log.due_date}</TableCell>
                      <TableCell>{format(new Date(log.sent_at), 'MMM d, yyyy h:mm a')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminReminders;
